import React, { useCallback, useMemo, useState } from 'react';
import {
  Avatar,
  Card,
  Chip,
  ChevronRightIcon,
  Collapse,
  PeopleIcon,
  PersonIcon,
  PersonAddIcon,
  Text,
  cn,
} from '@billda/design-system';
import { formatCurrency } from '../../../../shared/utils/currency';
import type {
  GangMemberSplit,
  FixedRateWorker,
} from '../../types/potDetails.types';

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '';
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const listRowClasses =
  'flex w-full cursor-pointer items-center gap-3 border-b border-border-default px-4 py-3 text-left hover:bg-surface-raised sm:px-6';

interface PotWorkersCardProps {
  gangMemberSplits: GangMemberSplit[];
  fixedRateWorkers: FixedRateWorker[];
  onWorkerClick?: (userId: string) => void;
  className?: string;
}

interface WorkerGroup {
  key: string;
  label: string;
  icon: React.ElementType;
  count: number;
  totalAmount: number;
}

export function PotWorkersCard({
  gangMemberSplits,
  fixedRateWorkers,
  onWorkerClick,
  className,
}: PotWorkersCardProps) {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set()
  );

  const toggleGroup = useCallback((key: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const fixedRateWorkerIds = useMemo(
    () => new Set(fixedRateWorkers.map(w => w.userId)),
    [fixedRateWorkers]
  );

  const leaderLookup = useMemo(
    () => new Map(gangMemberSplits.map(w => [w.userId, w.isLeader])),
    [gangMemberSplits]
  );

  const newMemberLookup = useMemo(
    () => new Map(gangMemberSplits.map(w => [w.userId, w.isNewMember])),
    [gangMemberSplits]
  );

  const percentageSplitWorkers = useMemo(
    () =>
      gangMemberSplits
        .filter(w => !fixedRateWorkerIds.has(w.userId))
        .sort((a, b) => {
          if (b.amount !== a.amount) return b.amount - a.amount;
          if (a.isLeader !== b.isLeader) return a.isLeader ? -1 : 1;
          return 0;
        }),
    [gangMemberSplits, fixedRateWorkerIds]
  );

  const fixedRateGangMembers = useMemo(
    () =>
      fixedRateWorkers
        .filter(w => w.isGangMember)
        .sort((a, b) => {
          if (b.totalAmount !== a.totalAmount)
            return b.totalAmount - a.totalAmount;
          const aIsLeader = leaderLookup.get(a.userId) ?? false;
          const bIsLeader = leaderLookup.get(b.userId) ?? false;
          if (aIsLeader !== bIsLeader) return aIsLeader ? -1 : 1;
          return 0;
        }),
    [fixedRateWorkers, leaderLookup]
  );

  const adHocWorkers = useMemo(
    () =>
      fixedRateWorkers
        .filter(w => !w.isGangMember)
        .sort((a, b) => b.totalAmount - a.totalAmount),
    [fixedRateWorkers]
  );

  const totalWorkers =
    percentageSplitWorkers.length +
    fixedRateGangMembers.length +
    adHocWorkers.length;

  if (totalWorkers === 0) return null;

  const groups: WorkerGroup[] = [];
  if (percentageSplitWorkers.length > 0) {
    groups.push({
      key: 'percentage',
      label: 'Percentage Split',
      icon: PeopleIcon,
      count: percentageSplitWorkers.length,
      totalAmount: percentageSplitWorkers.reduce((s, w) => s + w.amount, 0),
    });
  }
  if (fixedRateGangMembers.length > 0) {
    groups.push({
      key: 'fixed',
      label: 'Fixed Rate',
      icon: PersonIcon,
      count: fixedRateGangMembers.length,
      totalAmount: fixedRateGangMembers.reduce((s, w) => s + w.totalAmount, 0),
    });
  }
  if (adHocWorkers.length > 0) {
    groups.push({
      key: 'adhoc',
      label: 'Ad-hoc',
      icon: PersonAddIcon,
      count: adHocWorkers.length,
      totalAmount: adHocWorkers.reduce((s, w) => s + w.totalAmount, 0),
    });
  }

  return (
    <Card data-testid="pot-workers-card" className={cn('h-full', className)}>
      <Text variant="h6" className="px-4 pb-2 pt-4 sm:px-6 sm:pt-6">
        Workers ({totalWorkers})
      </Text>

      {groups.map(group => {
        const expanded = !collapsedGroups.has(group.key);
        const GroupIcon = group.icon;
        return (
          <div key={group.key}>
            <button
              type="button"
              onClick={() => {
                toggleGroup(group.key);
              }}
              aria-expanded={expanded}
              className="flex w-full cursor-pointer items-center justify-start gap-2 bg-surface-raised px-4 py-2 text-left transition-colors hover:bg-surface-pressed sm:px-6"
            >
              <ChevronRightIcon
                aria-hidden="true"
                className={cn(
                  'size-4 text-content-secondary transition-transform duration-150',
                  expanded && 'rotate-90'
                )}
              />
              <GroupIcon
                aria-hidden="true"
                className="size-4 shrink-0 text-content-secondary"
              />
              <Text variant="body2" className="font-semibold">
                {group.label}
              </Text>
              <Text variant="body2" color="secondary">
                {group.count}
              </Text>
              <Text variant="body2" className="ml-auto font-semibold">
                {formatCurrency(group.totalAmount)}
              </Text>
            </button>

            <Collapse in={expanded} unmountOnExit>
              {group.key === 'percentage' &&
                percentageSplitWorkers.map(worker => (
                  <PercentageWorkerListItem
                    key={worker.userId}
                    worker={worker}
                    onClick={onWorkerClick}
                  />
                ))}
              {group.key === 'fixed' &&
                fixedRateGangMembers.map(worker => (
                  <FixedRateWorkerListItem
                    key={worker.userId}
                    worker={worker}
                    isLeader={leaderLookup.get(worker.userId) ?? false}
                    isNewMember={newMemberLookup.get(worker.userId) ?? false}
                    onClick={onWorkerClick}
                  />
                ))}
              {group.key === 'adhoc' &&
                adHocWorkers.map(worker => (
                  <AdHocWorkerListItem
                    key={worker.userId}
                    worker={worker}
                    onClick={onWorkerClick}
                  />
                ))}
            </Collapse>
          </div>
        );
      })}
    </Card>
  );
}

function handleRowKeyDown(
  event: React.KeyboardEvent,
  activate: () => void
): void {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    activate();
  }
}

const PercentageWorkerListItem = React.memo(function PercentageWorkerListItem({
  worker,
  onClick,
}: {
  worker: GangMemberSplit;
  onClick?: (userId: string) => void;
}) {
  const percentageDisplay = `${Math.round(worker.percentage * 100)}%`;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick?.(worker.userId)}
      onKeyDown={event => {
        handleRowKeyDown(event, () => onClick?.(worker.userId));
      }}
      className={listRowClasses}
    >
      <Avatar
        size="sm"
        className="shrink-0 bg-surface-raised text-xs font-semibold text-content-secondary"
      >
        {getInitials(worker.userName)}
      </Avatar>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Text variant="body2" className="truncate font-semibold">
            {worker.userName}
          </Text>
          {worker.isLeader && (
            <Chip
              label="Leader"
              size="sm"
              color="primary"
              className="h-5 shrink-0 text-[0.7rem]"
            />
          )}
          {worker.isNewMember && (
            <Chip
              label="New"
              size="sm"
              color="warning"
              variant="outlined"
              className="h-5 shrink-0 text-[0.7rem]"
            />
          )}
          {worker.isFormerMember && (
            <Chip
              label="Former"
              size="sm"
              color="warning"
              variant="outlined"
              className="h-5 shrink-0 text-[0.7rem]"
            />
          )}
        </div>
        <Text variant="caption" color="secondary">
          {percentageDisplay} split
        </Text>
      </div>

      <Text
        variant="body2"
        className="shrink-0 whitespace-nowrap font-semibold"
      >
        {formatCurrency(worker.amount)}
      </Text>
    </div>
  );
});

const FixedRateWorkerListItem = React.memo(function FixedRateWorkerListItem({
  worker,
  isLeader,
  isNewMember,
  onClick,
}: {
  worker: FixedRateWorker;
  isLeader: boolean;
  isNewMember: boolean;
  onClick?: (userId: string) => void;
}) {
  const calculation =
    worker.bonusAmount > 0
      ? `${formatCurrency(worker.rateValue)} × ${worker.unitsWorked} + ${formatCurrency(worker.bonusAmount)}`
      : `${formatCurrency(worker.rateValue)} × ${worker.unitsWorked}`;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick?.(worker.userId)}
      onKeyDown={event => {
        handleRowKeyDown(event, () => onClick?.(worker.userId));
      }}
      className={listRowClasses}
    >
      <Avatar
        size="sm"
        className="shrink-0 bg-surface-raised text-xs font-semibold text-content-secondary"
      >
        {getInitials(worker.userName)}
      </Avatar>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Text variant="body2" className="truncate font-semibold">
            {worker.userName}
          </Text>
          {isLeader && (
            <Chip
              label="Leader"
              size="sm"
              color="primary"
              className="h-5 shrink-0 text-[0.7rem]"
            />
          )}
          {isNewMember && (
            <Chip
              label="New"
              size="sm"
              color="warning"
              variant="outlined"
              className="h-5 shrink-0 text-[0.7rem]"
            />
          )}
          {worker.isFormerMember && (
            <Chip
              label="Former"
              size="sm"
              color="warning"
              variant="outlined"
              className="h-5 shrink-0 text-[0.7rem]"
            />
          )}
        </div>
        <Text variant="caption" color="secondary">
          {calculation}
        </Text>
      </div>

      <Text
        variant="body2"
        className="shrink-0 whitespace-nowrap font-semibold"
      >
        {formatCurrency(worker.totalAmount)}
      </Text>
    </div>
  );
});

const AdHocWorkerListItem = React.memo(function AdHocWorkerListItem({
  worker,
  onClick,
}: {
  worker: FixedRateWorker;
  onClick?: (userId: string) => void;
}) {
  const calculation =
    worker.bonusAmount > 0
      ? `${formatCurrency(worker.rateValue)} × ${worker.unitsWorked} + ${formatCurrency(worker.bonusAmount)}`
      : `${formatCurrency(worker.rateValue)} × ${worker.unitsWorked}`;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick?.(worker.userId)}
      onKeyDown={event => {
        handleRowKeyDown(event, () => onClick?.(worker.userId));
      }}
      className={listRowClasses}
    >
      <Avatar
        size="sm"
        className="shrink-0 bg-surface-raised text-xs font-semibold text-content-secondary"
      >
        {getInitials(worker.userName)}
      </Avatar>

      <div className="min-w-0 flex-1">
        <Text variant="body2" className="truncate font-semibold">
          {worker.userName}
        </Text>
        <Text variant="caption" color="secondary">
          {calculation}
        </Text>
      </div>

      <Text
        variant="body2"
        className="shrink-0 whitespace-nowrap font-semibold"
      >
        {formatCurrency(worker.totalAmount)}
      </Text>
    </div>
  );
});
